import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Popover,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useTemplateSx from '../../hooks/useTemplateSx';
import { templateService } from '../../services/templateService';
import TemplateOrganisationSearchableSelect from '../shared/TemplateOrganisationSearchableSelect';
import TemplateBotNumberSearchableSelect from '../shared/TemplateBotNumberSearchableSelect';
import TemplateSearchableSelect from '../shared/TemplateSearchableSelect';
import { DEFAULT_QUEUE_FILTERS } from '../../utils/templateQueueFilters';

function FilterField({ label, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function mapTemplateOption(template) {
  const value = String(template?.wa_Id ?? template?.wa_id ?? '').trim();
  if (!value) return null;
  return {
    label: template?.name || value,
    value,
    wa_Id: value,
    name: template?.name || value,
  };
}

function buildDraft(filters = {}) {
  return {
    organisation: filters.organisation ?? '',
    organisationObj: filters.organisationObj ?? null,
    botNumber: filters.botNumber ?? '',
    botNumberObj: filters.botNumberObj ?? null,
    templateId: filters.templateId ?? '',
    templateObj: filters.templateObj ?? null,
  };
}

export default function TemplateQueueFilters({ filters, onChange, onClear }) {
  const styles = useTemplateSx();
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(() => buildDraft(filters));
  const [templateOptions, setTemplateOptions] = useState([]);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !draft.organisation || !draft.botNumber) {
      setTemplateOptions([]);
      return undefined;
    }

    let cancelled = false;
    templateService.getAllTemplates({
      organisation_id: draft.organisation,
      bot_number: draft.botNumber,
    }).then((res) => {
      if (cancelled) return;
      const payload = res?.data?.data ?? res?.data ?? {};
      const raw = Array.isArray(payload) ? payload : (payload.list ?? payload.results ?? []);
      const results = Array.isArray(raw) ? raw : [];
      setTemplateOptions(results.map(mapTemplateOption).filter(Boolean));
    }).catch(() => {
      if (!cancelled) setTemplateOptions([]);
    });

    return () => {
      cancelled = true;
    };
  }, [open, draft.organisation, draft.botNumber]);

  const activeCount = [filters.organisation, filters.botNumber, filters.templateId].filter(Boolean).length;
  const chips = useMemo(() => {
    const next = [];
    if (filters.organisation) {
      next.push({
        key: 'organisation',
        label: filters.organisationObj?.organisation_name
          || filters.organisationObj?.label
          || filters.organisation,
      });
    }
    if (filters.botNumber) {
      next.push({
        key: 'botNumber',
        label: `Channel: ${filters.botNumberObj?.wa_phone_number || filters.botNumberObj?.label || filters.botNumber}`,
      });
    }
    if (filters.templateId) {
      next.push({
        key: 'template',
        label: `Template: ${filters.templateObj?.label || filters.templateObj?.name || filters.templateId}`,
      });
    }
    return next;
  }, [filters]);

  const updateDraft = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'organisation') && patch.organisation !== prev.organisation) {
        next.botNumber = '';
        next.botNumberObj = null;
        next.templateId = '';
        next.templateObj = null;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'botNumber') && patch.botNumber !== prev.botNumber) {
        next.templateId = '';
        next.templateObj = null;
      }
      return next;
    });
  };

  const handleOpen = (event) => {
    setDraft(buildDraft(filters));
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange({ ...draft, page: 0 });
    handleClose();
  };

  const handleResetDraft = () => {
    setDraft(buildDraft(DEFAULT_QUEUE_FILTERS));
    onClear();
    handleClose();
  };

  return (
    <Box sx={styles.filtersToolbar}>
      <Box sx={styles.filtersBar}>
        <Badge
          badgeContent={activeCount}
          color="primary"
          invisible={activeCount === 0}
          sx={{ flexShrink: 0 }}
        >
          <Button
            variant={open || activeCount > 0 ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FilterListIcon fontSize="small" />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '18px !important' }} />}
            onClick={handleOpen}
            sx={styles.filterToggleButton}
          >
            Filters
          </Button>
        </Badge>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { sx: styles.filterPopoverPaper },
          }}
        >
          <Box sx={styles.filterPopoverContent}>
            <Box sx={styles.filterPopoverHeader}>
              <Typography sx={styles.filterPopoverTitle}>Filter queue</Typography>
              <Typography sx={styles.filterPopoverSubtitle}>
                Select criteria and click Apply
              </Typography>
            </Box>

            <Box sx={styles.filterPopoverGrid}>
              <FilterField label="Organisation">
                <TemplateOrganisationSearchableSelect
                  label=""
                  placeholder="All organisations"
                  valueId={draft.organisation || null}
                  selectedOrganisation={draft.organisationObj ?? null}
                  onChange={(option) => {
                    updateDraft({
                      organisation: option?.organisation_id ?? option?.id ?? option?.value ?? '',
                      organisationObj: option,
                    });
                  }}
                />
              </FilterField>

              <FilterField label="Channel number">
                <TemplateBotNumberSearchableSelect
                  label=""
                  placeholder={draft.organisation ? 'All channel numbers' : 'Select an organisation first'}
                  organisation={draft.organisationObj}
                  valueId={draft.botNumber || null}
                  selectedBot={draft.botNumberObj ?? null}
                  disabled={!draft.organisation}
                  onChange={(option) => {
                    updateDraft({
                      botNumber: option?.wa_phone_number ?? option?.value ?? '',
                      botNumberObj: option,
                    });
                  }}
                />
              </FilterField>

              <FilterField label="Template">
                <TemplateSearchableSelect
                  variant="filter"
                  label=""
                  placeholder={draft.botNumber ? 'All templates' : 'Select a channel first'}
                  options={templateOptions}
                  value={draft.templateObj ?? null}
                  disabled={!draft.organisation || !draft.botNumber}
                  onChange={(option) => {
                    updateDraft({
                      templateId: option?.value ?? '',
                      templateObj: option,
                    });
                  }}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                />
              </FilterField>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={styles.filterPopoverActions}>
              <Button size="small" onClick={handleResetDraft} sx={styles.filterPopoverResetButton}>
                Reset
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleApply}>
                  Apply filters
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      {chips.length ? (
        <Box sx={styles.activeFilterChipsRow}>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              onDelete={() => onChange({ clear: chip.key })}
              sx={styles.activeFilterChip}
            />
          ))}
          <Button
            variant="text"
            size="small"
            onClick={onClear}
            sx={styles.filterClearAllLink}
          >
            Clear all
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
